import {
  WaveCloudAPI,
  WAVE_APP_ID,
  WAVE_CHAT_AGENT_NAME,
  WAVE_CHAT_AGENT_SUB,
  assistantPromptFromComment,
  type WaveCommentRecord,
  type WaveDocEditor,
} from "@vida/wave-core";
import { pollAIJob } from "./waveAiJobHelpers";
import { WAVE_AGENT_MODEL_ID } from "./waveAgentModel";
import { createWaveApiConfig, trackActivity } from "./waveSession";

export async function runWaveCommentAgent(params: {
  waveId: string;
  blockAnchorId: string;
  parentCommentId: string;
  galleryItemId?: string | null;
  userPrompt: string;
  recentComments: WaveCommentRecord[];
}): Promise<string> {
  const prompt = assistantPromptFromComment(params.userPrompt) || params.userPrompt.trim();
  if (!prompt) throw new Error("Prompt assistente vuoto.");

  const scoped = params.recentComments
    .filter((c) => c.blockAnchorId === params.blockAnchorId)
    .slice(-12);
  const transcript =
    scoped.map((c) => `${c.authorName}: ${c.body}`).join("\n") || "(vuoto)";

  const cfg = createWaveApiConfig();
  const body = {
    bedrock: {
      systemText:
        "Sei l'assistente Wave nei commenti su un widget del thread. " +
        "Rispondi in italiano, conciso e utile. Non usare markdown pesante.",
      userText:
        `COMMENTI RECENTI SUL WIDGET:\n${transcript}\n\n` +
        `RICHIESTA (menzione @Assistente):\n${prompt}`,
      maxTokens: 2048,
    },
    waveId: params.waveId,
    blockAnchorId: params.blockAnchorId,
    parentCommentId: params.parentCommentId,
    galleryItemId: params.galleryItemId ?? null,
  };

  const result = await WaveCloudAPI.submitAIJob(cfg, {
    kind: "BEDROCK_CONVERSE",
    provider: "BEDROCK",
    prompt,
    paramsJson: JSON.stringify(body),
    inputAssetKeys: [],
    appId: WAVE_APP_ID,
    modelId: WAVE_AGENT_MODEL_ID,
    feature: "wave.agent.comment",
  });

  trackActivity("Commenti", `Assistente (job ${result.jobId.slice(0, 8)}…)`);
  return pollAIJob(result.jobId);
}

export async function postAssistantCommentReply(params: {
  editor: WaveDocEditor;
  blockAnchorId: string;
  parentCommentId: string;
  galleryItemId?: string | null;
  answer: string;
}): Promise<void> {
  const trimmed = params.answer.trim();
  if (!trimmed) return;
  params.editor.postComment(
    params.blockAnchorId,
    trimmed,
    params.parentCommentId,
    WAVE_CHAT_AGENT_SUB,
    WAVE_CHAT_AGENT_NAME,
    params.galleryItemId ?? null,
  );
}

export async function maybeRunCommentAgentReply(params: {
  editor: WaveDocEditor;
  waveId: string;
  blockAnchorId: string;
  postedComment: WaveCommentRecord;
  body: string;
  allComments: WaveCommentRecord[];
}): Promise<void> {
  try {
    const answer = await runWaveCommentAgent({
      waveId: params.waveId,
      blockAnchorId: params.blockAnchorId,
      parentCommentId: params.postedComment.id,
      galleryItemId: params.postedComment.galleryItemId,
      userPrompt: params.body,
      recentComments: params.allComments,
    });
    await postAssistantCommentReply({
      editor: params.editor,
      blockAnchorId: params.blockAnchorId,
      parentCommentId: params.postedComment.id,
      galleryItemId: params.postedComment.galleryItemId,
      answer,
    });
    trackActivity("Commenti", "Risposta Assistente pubblicata nel thread commenti.");
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Assistente non disponibile";
    trackActivity("Commenti", msg);
  }
}
